'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { useSettings } from '@/providers/settings-provider';

const containerVariants = cva('w-full mx-auto px-4 lg:px-6', {
  variants: {
    width: {
      fixed: 'max-w-[1320px]',
      fluid: '',
    },
  },
  defaultVariants: {
    width: 'fixed',
  },
});

export interface ContainerProps extends VariantProps<typeof containerVariants> {
  children?: ReactNode;
  width?: 'fixed' | 'fluid';
  className?: string;
}

const ContainerWidthContext = createContext<'fixed' | 'fluid' | undefined>(
  undefined,
);

export function ContainerWidthProvider({
  children,
  width,
}: {
  children: ReactNode;
  width: 'fixed' | 'fluid';
}) {
  return (
    <ContainerWidthContext.Provider value={width}>
      {children}
    </ContainerWidthContext.Provider>
  );
}

export function Container({ children, width, className = '' }: ContainerProps) {
  const { settings } = useSettings();
  const overrideWidth = useContext(ContainerWidthContext);
  const effectiveWidth =
    width ?? overrideWidth ?? settings.container ?? 'fixed';

  return (
    <div
      data-slot="container"
      className={cn(
        containerVariants({ width: effectiveWidth }),
        !width && overrideWidth === 'fluid' && 'lg:px-8',
        className,
      )}
    >
      {children}
    </div>
  );
}
